import React, { useMemo } from "react";
import { connect } from "react-redux";
import { Dispatch } from "redux";
import styled from "styled-components";
import media from "styled-media-query";

import { State } from "src/state/ducks";
import { BakedBreadEntity } from "src/firebase/db/BakedBreads";
import { OrderBreadEntity } from "src/firebase/db/OrderBreads";
import { NotificationEntity } from "src/firebase/db/Notifications";

import StyleConfig from "src/constants/StyleConfig";
import { formatDate } from "src/helpers/UtilityHelper";

import Button from "src/components/atoms/button/Button";
import PlainHomeButton from "src/components/atoms/button/PlainHomeButton";

type EntityProps = {
  notification: NotificationEntity;
  orderBread: OrderBreadEntity;
  bakedBread: BakedBreadEntity;
};

type Props = {
  bakedBread: BakedBreadEntity;
  orderBread: OrderBreadEntity;
  notification: NotificationEntity;
  onBack: (payload: EntityProps) => void;
  onCancel: () => void;
  onSubmit: (payload: EntityProps) => void;
};

const dateFormat = "YYYY年MM月DD日(ddd) HH:mm";

const ReservationConfirmForm = (props: Props) => {
  const { bakedBread, orderBread, notification, onSubmit, onBack } = props;

  const {
    name,
    price,
    image,
    description,
    width,
    depth,
    height,
    best_before,
    baked_bread_tags,
  } = bakedBread;

  const { stock, maximum_order, shop_name } = orderBread;

  const {
    published_time,
    scheduled_time,
    can_order_time,
    last_pickup_days,
    pickup_supplement,
  } = notification;

  const lastPickupDays = last_pickup_days as (Date | undefined)[];

  const categeoryName = useMemo(
    () =>
      baked_bread_tags && baked_bread_tags.length
        ? baked_bread_tags[0].name
        : "",
    [baked_bread_tags]
  );

  return (
    <Styled>
      <Inner>
        <Item>
          <Label>商品画像</Label>
          <Value>
            <img src={image} alt="" role="presentation" />
          </Value>
        </Item>
        <Item>
          <Label>対象店舗</Label>
          <Value>{shop_name}</Value>
        </Item>
        <Item>
          <Label>商品名</Label>
          <Value>{name}</Value>
        </Item>
        <Item>
          <Label>カテゴリー</Label>
          <Value>{categeoryName}</Value>
        </Item>
        <Item>
          <Label>販売単価（税抜）</Label>
          <Value>{price}円</Value>
        </Item>
        <Item>
          <Label>商品説明</Label>
          <Description>{description}</Description>
        </Item>
        <Item>
          <Label>商品サイズ(cm)</Label>
          <Value>
            幅：{width || "-"}cm・奥行き：{depth || "-"}cm・高さ：
            {height || "-"}cm
          </Value>
        </Item>
        <Item>
          <Label>賞味期限</Label>
          <Value>{best_before}</Value>
        </Item>
        <Item>
          <Label>総販売数</Label>
          <Value>{stock}個</Value>
        </Item>
        <Item>
          <Label>一人のお客さまが購入できる最大数</Label>
          <Value>{maximum_order}個</Value>
        </Item>
        <Item>
          <Label>事前告知開始日時</Label>
          <Value>{formatDate(published_time, dateFormat)}</Value>
        </Item>
        <Item>
          <Label>予約注文開始日時（注文解禁日）</Label>
          <Value>{formatDate(scheduled_time, dateFormat)}</Value>
        </Item>
        <Item>
          <Label>ラストオーダー日時</Label>
          <Value>{formatDate(can_order_time, dateFormat)}</Value>
        </Item>
        <Item>
          <Label>受け渡し日</Label>
          <Value>
            {lastPickupDays.map((v, i) => (
              <div key={`last_pickup_days|${i}`}>
                {formatDate(v, "YYYY年MM月DD日(ddd)")}
              </div>
            ))}
          </Value>
        </Item>
        <Item>
          <Label>商品を受け渡す際の注意事項</Label>
          <Value>{pickup_supplement || "なし"}</Value>
        </Item>
      </Inner>
      <Controller>
        <PrevButtonWrapper>
          <Button
            isDisabled={false}
            buttonColor="green"
            clickEvent={() => {
              onSubmit({
                bakedBread,
                orderBread,
                notification,
              });
            }}
            text="配信する"
          />
        </PrevButtonWrapper>
        <NextButtonWrapper>
          <Button
            text="内容を修正する"
            buttonColor="white"
            textColor="green"
            borderColor="green"
            isDisabled={false}
            clickEvent={onBack}
          />
        </NextButtonWrapper>

        <HomeButtonWrapper>
          <PlainHomeButton text="編集をキャンセルしトップページに戻る" />
        </HomeButtonWrapper>
      </Controller>
    </Styled>
  );
};

const Styled = styled.div`
  box-sizing: border-box;
  width: 100%;
  ${media.lessThan("small")`
    width: 100vw;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  `}
`;
const Inner = styled.div`
  background-color: #fff;
  width: 100%;
  padding-bottom: 20px;
  ${media.lessThan("small")`
    width: 100vw;
    position: relative;
    left: 50%;
    transform: translateX(-50%);
  `}
`;
const Item = styled.div`
  &:not(:first-child) {
    margin-top: 16px;
  }
`;
const Label = styled.div`
  font-size: 14px;
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  background: ${StyleConfig.COLOR.INPUT_BORDER};
  padding: 9px ${StyleConfig.HELPER.M16};
`;
const Value = styled.div`
  font-size: 16px;
  line-height: ${24 / 16};
  font-weight: bold;
  color: ${StyleConfig.COLOR.TEXT_COLOR};
  padding: 16px ${StyleConfig.HELPER.M16} 0;
`;
const Description = styled(Value as any)`
  white-space: pre-wrap;
`;

const Controller = styled.div`
  padding: 0;
  margin: 32px auto;
  ${media.lessThan("small")`
    padding: 0 ${StyleConfig.HELPER.M16};
  `}
`;
const PrevButtonWrapper = styled.div`
  width: 100%;
  margin-bottom: 20px;
`;
const NextButtonWrapper = styled.div`
  width: 100%;
`;
const HomeButtonWrapper = styled.div`
  width: 100%;
  margin-top: 36px;
`;

const mapStateToProps = (state: State) => ({});

const mapDispatchToProps = (dispatch: Dispatch) => ({});
export default connect(
  mapStateToProps,
  mapDispatchToProps
)(ReservationConfirmForm);
